import React, { useEffect, useState, useContext } from 'react';
import { Box, Typography, Button, Fade, Chip } from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import GavelIcon from '@mui/icons-material/Gavel';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import BalanceIcon from '@mui/icons-material/Balance';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import CloseIcon from '@mui/icons-material/Close';
import AuthContext from '../contexts/AuthContext';

// Scade il 19 marzo 2026 alle 12:00
const EXPIRY = new Date('2026-03-19T12:00:00');

const features = [
  {
    icon: <AutoAwesomeIcon sx={{ fontSize: 20 }} />,
    label: 'BOOKY SEARCH',
    desc: 'Ricerca AI su 200.000+ sentenze in linguaggio naturale',
    color: '#a3e635',
    bg: 'linear-gradient(135deg, #0d1f0a, #1a3510)',
    border: 'rgba(132,204,22,0.3)',
  },
  {
    icon: <GavelIcon sx={{ fontSize: 20 }} />,
    label: 'BOOKY TRIBUNALE',
    desc: 'Orientamenti giurisprudenziali per sede',
    color: '#c084fc',
    bg: 'linear-gradient(135deg, #160a24, #251040)',
    border: 'rgba(168,85,247,0.3)',
  },
  {
    icon: <WorkspacePremiumIcon sx={{ fontSize: 20 }} />,
    label: 'BOOKY WORK',
    desc: 'Fascicoli digitali con AI integrata per il tuo caso',
    color: '#60a5fa',
    bg: 'linear-gradient(135deg, #071524, #0d2245)',
    border: 'rgba(37,99,235,0.3)',
  },
  {
    icon: <BalanceIcon sx={{ fontSize: 20 }} />,
    label: 'BOOKY GIUDICE',
    desc: 'Il primo AI che simula il profilo di ogni giudice italiano',
    color: '#fbbf24',
    bg: 'linear-gradient(135deg, #1a0f02, #2e1c06)',
    border: 'rgba(217,119,6,0.3)',
    isNew: true,
  },
];

// Confetti particle
const Particle = ({ style }: { style: React.CSSProperties }) => (
  <Box sx={{
    position: 'fixed',
    width: '7px', height: '7px',
    borderRadius: '1px',
    animation: 'confettiFall linear infinite',
    '@keyframes confettiFall': {
      '0%': { transform: 'translateY(-20px) rotate(0deg)', opacity: 1 },
      '100%': { transform: 'translateY(110vh) rotate(720deg)', opacity: 0 },
    },
    ...style,
  }} />
);

// Stella lampeggiante
const Star = ({ style }: { style: React.CSSProperties }) => (
  <Box sx={{
    position: 'fixed',
    fontSize: '18px',
    animation: 'starBlink ease-in-out infinite',
    '@keyframes starBlink': {
      '0%, 100%': { opacity: 0.1, transform: 'scale(0.7)' },
      '50%': { opacity: 1, transform: 'scale(1.3)' },
    },
    pointerEvents: 'none',
    userSelect: 'none',
    ...style,
  }}>⭐</Box>
);

const CONFETTI_COLORS = ['#a3e635', '#c084fc', '#60a5fa', '#fbbf24', '#f43f5e', '#34d399', '#fb923c', '#e879f9'];
const particles = Array.from({ length: 55 }, (_, i) => ({
  left: `${Math.random() * 100}%`,
  top: 0,
  animationDuration: `${2 + Math.random() * 3.5}s`,
  animationDelay: `${Math.random() * 4}s`,
  background: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
  width: `${5 + Math.random() * 7}px`,
  height: `${5 + Math.random() * 7}px`,
  borderRadius: Math.random() > 0.5 ? '50%' : '2px',
  zIndex: 9998,
}));

const stars = Array.from({ length: 12 }, (_, i) => ({
  left: `${5 + i * 8}%`,
  top: `${10 + Math.sin(i) * 25}%`,
  animationDuration: `${1.2 + Math.random() * 1.8}s`,
  animationDelay: `${Math.random() * 2}s`,
  zIndex: 9998,
}));

export default function V1CelebrationModal() {
  const [open, setOpen] = useState(false);
  const [closing, setClosing] = useState(false);
  const { token, user } = useContext(AuthContext);
  const isLoggedIn = !!token && !!user;

  useEffect(() => {
    const now = new Date();
    if (now >= EXPIRY) return; // scaduto il 19 marzo alle 12:00
    if (isLoggedIn) {
      setTimeout(() => setOpen(true), 900);
    } else {
      setOpen(false);
    }
  }, [isLoggedIn]);

  const handleClose = () => {
    setClosing(true);
    setTimeout(() => setOpen(false), 300);
  };

  if (!open) return null;

  return (
    <Fade in={!closing} timeout={300}>
      <Box sx={{
        position: 'fixed', inset: 0, zIndex: 9999,
        background: 'rgba(5, 10, 20, 0.88)',
        backdropFilter: 'blur(8px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        p: 2, overflow: 'hidden',
      }}>
        {/* Confetti */}
        {particles.map((p, i) => (
          <Particle key={i} style={p as React.CSSProperties} />
        ))}
        {/* Stelle */}
        {stars.map((s, i) => (
          <Star key={`s${i}`} style={s as React.CSSProperties} />
        ))}

        {/* Modal */}
        <Box sx={{
          position: 'relative', zIndex: 1,
          background: 'linear-gradient(160deg, #0d1b2a 0%, #111e30 60%, #0a1520 100%)',
          border: '1.5px solid rgba(251,191,36,0.25)',
          borderRadius: '24px',
          boxShadow: '0 32px 80px rgba(0,0,0,0.7), 0 0 60px rgba(251,191,36,0.08)',
          maxWidth: 560, width: '100%',
          overflow: 'hidden',
          animation: 'popIn 0.5s cubic-bezier(0.34, 1.56, 0.64, 1)',
          '@keyframes popIn': {
            '0%': { opacity: 0, transform: 'scale(0.8) translateY(30px)' },
            '100%': { opacity: 1, transform: 'scale(1) translateY(0)' },
          },
        }}>

          {/* Glow top */}
          <Box sx={{
            position: 'absolute', top: -60, left: '50%', transform: 'translateX(-50%)',
            width: 300, height: 160,
            background: 'radial-gradient(ellipse, rgba(251,191,36,0.18) 0%, transparent 70%)',
            pointerEvents: 'none',
          }} />

          {/* Close */}
          <Box
            onClick={handleClose}
            sx={{
              position: 'absolute', top: 16, right: 16,
              width: 32, height: 32, borderRadius: '50%',
              background: 'rgba(255,255,255,0.06)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer', color: 'rgba(255,255,255,0.4)',
              transition: 'all 0.15s',
              '&:hover': { background: 'rgba(255,255,255,0.12)', color: '#fff' },
            }}
          >
            <CloseIcon sx={{ fontSize: 16 }} />
          </Box>

          <Box sx={{ p: { xs: 3, md: 4 } }}>

            {/* Header */}
            <Box sx={{ textAlign: 'center', mb: 3.5 }}>
              <Box sx={{
                display: 'inline-flex', alignItems: 'center', gap: 1,
                background: 'linear-gradient(135deg, rgba(251,191,36,0.15), rgba(218,165,32,0.08))',
                border: '1px solid rgba(251,191,36,0.3)',
                borderRadius: '99px', px: 2.5, py: 0.6, mb: 2.5,
              }}>
                <RocketLaunchIcon sx={{ fontSize: 14, color: '#fbbf24' }} />
                <Typography sx={{ color: '#fbbf24', fontSize: 12, fontWeight: 800, letterSpacing: '0.12em' }}>
                  VERSIONE 1.0.0 — UFFICIALMENTE LIVE
                </Typography>
              </Box>

              <Typography sx={{
                fontFamily: 'Syne, sans-serif', fontWeight: 800,
                fontSize: { xs: 28, md: 34 },
                color: '#fff',
                lineHeight: 1.15,
                letterSpacing: '-0.02em',
                mb: 1,
              }}>
                🎉 ITF Plus è{' '}
                <Box component="span" sx={{
                  background: 'linear-gradient(135deg, #fbbf24, #fb923c, #a3e635)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  animation: 'shimmer 2s ease-in-out infinite',
                  '@keyframes shimmer': {
                    '0%, 100%': { filter: 'brightness(1)' },
                    '50%': { filter: 'brightness(1.3)' },
                  },
                }}>
                  ufficialmente live
                </Box>
                {' '}🚀
              </Typography>

              <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: 13.5, lineHeight: 1.6, mb: 1 }}>
                4 strumenti AI per l'avvocato del futuro. Tutto in un posto.
              </Typography>
              <Typography sx={{
                color: 'rgba(251,191,36,0.6)', fontSize: 11.5,
                fontStyle: 'italic', letterSpacing: '0.04em',
              }}>
                🎊 Celebra con noi — offerta speciale fino al 19 marzo
              </Typography>
            </Box>

            {/* Feature list */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 3.5 }}>
              {features.map(f => (
                <Box key={f.label} sx={{
                  display: 'flex', alignItems: 'center', gap: 2,
                  background: f.bg,
                  border: `1.5px solid ${f.border}`,
                  borderRadius: '12px', p: 1.5,
                  transition: 'transform 0.15s',
                  '&:hover': { transform: 'translateX(4px)' },
                }}>
                  <Box sx={{
                    color: f.color,
                    background: f.border.replace('0.3', '0.12'),
                    borderRadius: '8px', p: 0.8,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    flexShrink: 0,
                  }}>
                    {f.icon}
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography sx={{
                        fontFamily: 'Syne, sans-serif', fontWeight: 800,
                        fontSize: 13, color: f.color, letterSpacing: '0.02em',
                      }}>
                        {f.label}
                      </Typography>
                      {f.isNew && (
                        <Chip label="NEW" size="small" sx={{
                          height: 16, fontSize: 9, fontWeight: 800,
                          background: 'linear-gradient(135deg, #fbbf24, #f59e0b)',
                          color: '#0d1b2a', letterSpacing: '0.08em',
                          '& .MuiChip-label': { px: 0.8 },
                        }} />
                      )}
                    </Box>
                    <Typography sx={{ color: 'rgba(255,255,255,0.45)', fontSize: 11.5, lineHeight: 1.4 }}>
                      {f.desc}
                    </Typography>
                  </Box>
                  <Typography sx={{ color: f.color, fontSize: 16, opacity: 0.4, flexShrink: 0 }}>→</Typography>
                </Box>
              ))}
            </Box>

            {/* CTA */}
            <Button
              fullWidth
              onClick={handleClose}
              sx={{
                background: 'linear-gradient(135deg, #B8860B, #DAA520, #fbbf24)',
                color: '#0d1b2a',
                fontFamily: 'Syne, sans-serif',
                fontWeight: 800,
                fontSize: 15,
                py: 1.6,
                borderRadius: '12px',
                textTransform: 'none',
                letterSpacing: '0.02em',
                boxShadow: '0 4px 24px rgba(184,134,11,0.45)',
                transition: 'all 0.2s cubic-bezier(0.34, 1.56, 0.64, 1)',
                '&:hover': {
                  transform: 'translateY(-3px) scale(1.01)',
                  boxShadow: '0 10px 36px rgba(184,134,11,0.6)',
                  background: 'linear-gradient(135deg, #DAA520, #fbbf24, #B8860B)',
                },
              }}
            >
              🎉 &nbsp; Entra in ITF Plus 1.0.0 &nbsp; 🚀
            </Button>

            <Typography sx={{ color: 'rgba(255,255,255,0.2)', fontSize: 10, textAlign: 'center', mt: 1.5 }}>
              Powered by ITF Plus · AI Giuridica Italiana
            </Typography>

          </Box>
        </Box>
      </Box>
    </Fade>
  );
}
